import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LuSearch,
  LuPlus,
  LuSlidersHorizontal,
  LuChevronDown,
  LuChevronRight,
  LuWallet,
  LuTrophy,
  LuBriefcase,
  LuX,
} from "react-icons/lu";
import WorkShell, { State } from "../components/AppPage";
import { useUser } from "../contexts/UserContext";
import {
  dateValue,
  isProUser,
  jobVisible,
  money,
  paid,
  rows,
  useRecord,
} from "../utils/appData";
import { useFunds } from "./Funds";
import ProPrice from "../components/ProPrice";
import JobCard from "../components/JobCard";

export function HomeFunds() {
  const { currentUser } = useUser();
  const funds = useFunds(currentUser?.uid);
  const now = new Date();
  const monthly = funds.list.filter((r) => {
    const date = new Date(dateValue(r.createdAt));
    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  });
  // Keep currencies separate rather than adding unlike amounts together.
  const totals = {};
  monthly.forEach((r) => {
    const currency = String(r.currency || "ZAR").toUpperCase();
    totals[currency] ||= { paid: 0, held: 0 };
    const amount = Number(r.amount ?? r.settledAmount ?? r.saleAmount ?? 0);
    if (Number.isFinite(amount))
      totals[currency][paid(r) ? "paid" : "held"] += amount;
  });
  const summary = Object.entries(totals)
    .map(
      ([c, t]) =>
        `${money(t.paid, c)} paid${t.held ? ` · ${money(t.held, c)} held` : ""}`,
    )
    .join(" · ");
  return (
    <Link
      to="/funds"
      className="mb-2.5 flex items-center gap-3 rounded-2xl border border-black/[0.06] bg-white p-3 dark:border-[#363e46] dark:bg-[#23272c]"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#e9f4ee] text-[#1e7f4f] dark:bg-[#16301f] dark:text-[#3fbf7f]">
        <LuWallet size={16} />
      </span>
      <span className="min-w-0 flex-1 [&>strong]:block [&>strong]:text-[14.5px] [&>strong]:font-bold [&>strong]:leading-[19px] [&>small]:mt-0.5 [&>small]:block [&>small]:text-[12.5px] [&>small]:leading-[17px] [&>small]:text-[#6e6e73] dark:[&>small]:text-[#9a9aa1]">
        <strong>Your funds</strong>
        <small>
          {funds.loading
            ? "Loading your funds…"
            : funds.error
              ? "Open your funds to try again"
              : summary || "Nothing yet this month — paid work lands here"}
        </small>
      </span>
      <LuChevronRight className="shrink-0 text-[#a0a0a8]" size={16} />
    </Link>
  );
}
export function HomePro() {
  const { currentUser } = useUser();
  if (isProUser(currentUser)) return null;
  return (
    <Link
      to="/subscription-plan"
      className="mb-2.5 flex items-center gap-3 rounded-2xl border border-black/[0.06] bg-white p-3 dark:border-[#363e46] dark:bg-[#23272c]"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[#edf3f5] text-[#b45309] dark:bg-[#22333a] dark:text-[#e0a33a]">
        <LuTrophy size={16} />
      </span>
      <span className="min-w-0 flex-1 [&>strong]:block [&>strong]:text-sm [&>strong]:font-semibold [&>strong]:leading-[18px] [&>small]:mt-0.5 [&>small]:block [&>small]:text-[11.5px] [&>small]:leading-4 [&>small]:text-[#6e6e73] dark:[&>small]:text-[#9a9aa1]">
        <strong>
          Get Pro, <ProPrice inline />
        </strong>
        <small>
          Unlimited applications, early access and priority placement.
        </small>
      </span>
      <LuChevronRight className="shrink-0 text-[#a0a0a8]" size={16} />
    </Link>
  );
}
const initialFilters = { city: "", role: "", remote: false };
export function selectJobs(data, user, { mode, search, city, role, remote }) {
  return rows(data)
    .filter((j) =>
      mode === "hire" ? j.hostId === user?.uid : jobVisible(j, user),
    )
    .filter((j) =>
      `${j.title || ""} ${j.role || ""} ${j.location || ""} ${j.companyName || ""}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    )
    .filter(
      (j) =>
        (!city || j.location === city) &&
        (!role || j.role === role) &&
        (!remote || /remote/i.test(j.location || "")),
    )
    .sort((a, b) => dateValue(b.createdAt) - dateValue(a.createdAt));
}
function Dashboard({ board = false }) {
  const { currentUser } = useUser();
  const jobs = useRecord("Jobs");
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState(
    params.get("mode") === "hire" ? "hire" : "work",
  );
  const [search, setSearch] = useState(board ? params.get("q") || "" : "");
  const [filters, setFilters] = useState(
    board
      ? {
          city: params.get("city") || "",
          role: params.get("role") || "",
          remote: params.get("remote") === "1",
        }
      : initialFilters,
  );
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const controls = () =>
      Array.from(panel.querySelectorAll("button,select,input,a[href]")).filter(
        (el) => !el.disabled,
      );
    const trap = (event) => {
      const items = controls();
      if (event.key !== "Tab" || !items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        items[items.length - 1].focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === items[items.length - 1]
      ) {
        event.preventDefault();
        items[0].focus();
      }
    };
    panel.addEventListener("keydown", trap);
    return () => panel.removeEventListener("keydown", trap);
  }, [open]);
  const list = selectJobs(jobs.data, currentUser, { mode, search, ...filters });
  const matched = (j) =>
    Boolean(
      currentUser?.niche &&
      String(j.role).toLowerCase() === String(currentUser.niche).toLowerCase(),
    );
  const ordered =
    mode === "work"
      ? [...list.filter(matched), ...list.filter((j) => !matched(j))]
      : list;
  const shown = board ? ordered : ordered.slice(0, 3);
  const source = rows(jobs.data).filter((j) =>
    mode === "hire"
      ? j.hostId === currentUser?.uid
      : jobVisible(j, currentUser),
  );
  const cities = [
    ...new Set(source.map((j) => j.location).filter(Boolean)),
  ].sort();
  const roles = [...new Set(source.map((j) => j.role).filter(Boolean))].sort();
  const count =
    Number(Boolean(filters.city)) +
    Number(Boolean(filters.role)) +
    Number(filters.remote);
  const destination = new URLSearchParams({
    mode,
    q: search,
    ...(filters.city ? { city: filters.city } : {}),
    ...(filters.role ? { role: filters.role } : {}),
    ...(filters.remote ? { remote: "1" } : {}),
  });
  const switchMode = (value) => {
    setMode(value);
    setFilters(initialFilters);
    if (board) setParams({ mode: value });
  };
  return (
    <WorkShell
      hideMobileHeading
      title={
        board ? (mode === "hire" ? "Your job posts" : "Find a job") : "Home"
      }
    >
      <section
        className="m-0 w-full min-w-0 max-w-none text-[#1d1d1f] dark:text-[#f5f5f7]"
        aria-label={board ? "Job board" : "Home dashboard"}
      >
        <div className="sticky top-0 z-[3] mb-3 bg-[var(--zylen-page-background,#f6f8fa)] pt-0.5 md:static md:pt-0">
          <div
            className="mb-3 flex h-11 w-full max-w-full rounded-[14px] bg-[#eaeaee] p-1 dark:bg-[#23272c] md:w-[360px] [&>button]:min-w-0 [&>button]:flex-1 [&>button]:basis-0 [&>button]:rounded-[11px] [&>button]:text-[14.5px] [&>button]:font-semibold [&>button]:text-[#6e6e73] [&>button]:transition-[background,box-shadow] dark:[&>button]:text-[#9a9aa1] [&>button[aria-pressed=true]]:bg-white [&>button[aria-pressed=true]]:font-bold [&>button[aria-pressed=true]]:text-[#1d1d1f] [&>button[aria-pressed=true]]:shadow-[0_1px_3px_#00000019] dark:[&>button[aria-pressed=true]]:bg-[#4a4a4e] dark:[&>button[aria-pressed=true]]:text-[#f5f5f7] motion-reduce:[&>button]:transition-none"
            aria-label="Job view"
          >
            <button
              aria-pressed={mode === "work"}
              onClick={() => switchMode("work")}
            >
              Find work
            </button>
            <button
              aria-pressed={mode === "hire"}
              onClick={() => switchMode("hire")}
            >
              Hire talent
            </button>
          </div>
          <label className="flex h-[42px] items-center gap-2.5 rounded-[14px] border border-black/[0.06] bg-white px-3.5 text-[#6e6e73] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#9a9aa1] md:h-[46px] [&>input]:w-full [&>input]:min-w-0 [&>input]:border-0 [&>input]:bg-transparent [&>input]:text-sm [&>input]:text-[#1d1d1f] [&>input]:outline-none [&>input:focus-visible]:outline-none dark:[&>input]:text-[#f5f5f7]">
            <LuSearch size={19} />
            <input
              type="text"
              aria-label="Search jobs"
              placeholder="Search by role, job or location"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                aria-label="Clear people search"
                onClick={() => setSearch("")}
              >
                <LuX size={18} />
              </button>
            )}
          </label>
          <div
            className="flex min-h-[58px] items-center gap-2 overflow-x-auto overscroll-x-contain py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>button]:inline-flex [&>button]:h-[34px] [&>button]:shrink-0 [&>button]:items-center [&>button]:gap-1.5 [&>button]:whitespace-nowrap [&>button]:rounded-[11px] [&>button]:border [&>button]:px-3 [&>button]:text-[13px] [&>button]:font-medium"
            aria-label="Job filters"
          >
            <button
              className="border-transparent bg-[#327691] text-white [&>b]:rounded-[9px] [&>b]:bg-white/25 [&>b]:px-[5px] [&>b]:text-[11px]"
              onClick={() => setOpen(true)}
            >
              <LuSlidersHorizontal size={14} />
              Filters{count > 0 && <b>{count}</b>}
            </button>
            <button
              onClick={() => setOpen(true)}
              className={
                filters.city
                  ? "border-[#327691] bg-[#edf3f5] text-[#327691] dark:border-[#5FAFCB] dark:bg-[#22333a] dark:text-[#5FAFCB]"
                  : "border-black/[0.06] bg-white text-[#6e6e73] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#9a9aa1]"
              }
            >
              {filters.city || "Anywhere"}
              <LuChevronDown size={13} />
            </button>
            <button
              onClick={() => setOpen(true)}
              className={
                filters.role
                  ? "border-[#327691] bg-[#edf3f5] text-[#327691] dark:border-[#5FAFCB] dark:bg-[#22333a] dark:text-[#5FAFCB]"
                  : "border-black/[0.06] bg-white text-[#6e6e73] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#9a9aa1]"
              }
            >
              {filters.role || "Any role"}
              <LuChevronDown size={13} />
            </button>
            <button
              aria-pressed={filters.remote}
              className={
                filters.remote
                  ? "border-[#327691] bg-[#edf3f5] text-[#327691] dark:border-[#5FAFCB] dark:bg-[#22333a] dark:text-[#5FAFCB]"
                  : "border-black/[0.06] bg-white text-[#6e6e73] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#9a9aa1]"
              }
              onClick={() =>
                setFilters({ ...filters, remote: !filters.remote })
              }
            >
              Remote only
            </button>
          </div>
        </div>
        {mode === "hire" && (
          <Link
            to="/post-job"
            className="mb-[18px] flex h-[52px] items-center justify-center gap-2 rounded-[15px] bg-[#327691] text-[15.5px] font-semibold text-white shadow-[0_2px_10px_#32769130]"
          >
            <LuPlus size={18} />
            Post a job
          </Link>
        )}
        <State loading={jobs.loading} error={jobs.error} />
        {!jobs.loading && !jobs.error && (
          <>
            {shown.length ? (
              <>
                <h2 className="mx-0.5 mb-[11px] text-[13px] font-semibold text-[#6e6e73] dark:text-[#9a9aa1]">
                  {mode === "hire"
                    ? "Your posts"
                    : shown.every(matched)
                      ? "Matched to you"
                      : "Latest jobs"}
                </h2>
                <div className="grid grid-cols-1 gap-x-3 gap-y-0 md:grid-cols-2 min-[1151px]:grid-cols-3">
                  {shown.map((j) => (
                    <JobCard
                      key={j.id}
                      job={j}
                      owned={mode === "hire"}
                      matched={mode === "work" && matched(j)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="mb-3.5 rounded-[20px] border border-black/[0.06] bg-white px-6 py-7 text-center dark:border-[#363e46] dark:bg-[#23272c] [&>h2]:mb-2 [&>h2]:text-lg [&>h2]:font-bold [&>p]:mx-auto [&>p]:mb-4 [&>p]:max-w-[370px] [&>p]:text-[13px] [&>p]:leading-5 [&>p]:text-[#6e6e73] dark:[&>p]:text-[#9a9aa1]">
                <span className="mx-auto mb-3.5 grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#edf3f5] text-[#327691] dark:bg-[#22333a] dark:text-[#5FAFCB]">
                  {mode === "hire" ? (
                    <LuBriefcase size={24} />
                  ) : (
                    <LuSearch size={24} />
                  )}
                </span>
                <h2>
                  {source.length
                    ? "No jobs match that"
                    : mode === "hire"
                      ? "Nothing posted yet"
                      : "No jobs yet"}
                </h2>
                <p>
                  {source.length
                    ? "Try another role or city, or clear your search and filters."
                    : mode === "hire"
                      ? "Post a job and reach creatives. Your posts and the people who apply show up here."
                      : "New jobs show up here as soon as they are approved."}
                </p>
                {source.length > 0 && (
                  <button
                    className="inline-flex min-h-11 items-center justify-center rounded-[14px] border border-black/[0.06] bg-white px-[17px] py-[11px] text-sm font-semibold text-inherit dark:border-[#363e46] dark:bg-[#23272c]"
                    onClick={() => {
                      setSearch("");
                      setFilters(initialFilters);
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
            {!board && (
              <Link
                className="mb-3.5 mt-1 flex h-12 items-center justify-center rounded-2xl border border-black/[0.06] bg-white text-sm font-semibold text-[#327691] shadow-[0_1px_2px_#0000000a] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#5FAFCB]"
                to={`/jobs?${destination}`}
              >
                {mode === "hire"
                  ? `Manage all ${list.length} post${list.length === 1 ? "" : "s"}`
                  : `See all ${list.length} job${list.length === 1 ? "" : "s"}`}
              </Link>
            )}
          </>
        )}
        {!board && (
          <div className="mt-3.5">
            <HomeFunds />
            <HomePro />
          </div>
        )}
      </section>
      {open && (
        <div
          className="fixed inset-0 z-[11000] grid items-end bg-black/50 md:place-items-center md:p-5"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
        >
          <section
            ref={panelRef}
            className="grid max-h-[85dvh] w-full gap-5 overflow-y-auto overscroll-contain rounded-t-3xl bg-white px-6 pt-6 pb-[max(24px,env(safe-area-inset-bottom))] text-[#1d1d1f] dark:bg-[#242426] dark:text-[#f5f5f7] md:max-w-[480px] md:rounded-3xl md:pb-6 [&_h2]:text-xl [&_h2]:font-extrabold [&_select]:w-full [&_select]:rounded-xl [&_select]:border [&_select]:border-slate-300 [&_select]:bg-transparent [&_select]:p-3 [&_select]:text-inherit dark:[&_select]:border-slate-600 dark:[&_option]:bg-[#242426]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="job-filter-title"
          >
            <div className="flex items-center justify-between gap-4">
              <h2 id="job-filter-title">Filter jobs</h2>
              <button
                autoFocus
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10"
                onClick={() => setOpen(false)}
                aria-label="Close job filters"
              >
                <LuX size={22} />
              </button>
            </div>
            <label className="grid gap-2 text-sm font-semibold">
              Location
              <select
                value={filters.city}
                onChange={(e) =>
                  setFilters({ ...filters, city: e.target.value })
                }
              >
                <option value="">Anywhere</option>
                {cities.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-2 text-sm font-semibold">
              Role
              <select
                value={filters.role}
                onChange={(e) =>
                  setFilters({ ...filters, role: e.target.value })
                }
              >
                <option value="">Any role</option>
                {roles.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={filters.remote}
                onChange={(e) =>
                  setFilters({ ...filters, remote: e.target.checked })
                }
              />
              Remote only
            </label>
            <div className="flex items-center justify-between gap-4">
              <button
                className="inline-flex min-h-11 items-center justify-center rounded-[14px] border border-black/[0.06] bg-white px-[17px] py-[11px] text-sm font-semibold text-inherit dark:border-[#363e46] dark:bg-[#23272c]"
                onClick={() => setFilters(initialFilters)}
              >
                Reset
              </button>
              <button
                className="inline-flex min-h-11 items-center justify-center rounded-[14px] bg-[#327691] px-[17px] py-[11px] text-sm font-semibold text-white"
                onClick={() => setOpen(false)}
              >
                Show {list.length} jobs
              </button>
            </div>
          </section>
        </div>
      )}
    </WorkShell>
  );
}
export default function HomeDashboard() {
  return <Dashboard />;
}
export function JobsBoard() {
  return <Dashboard board />;
}
